import { useState, useEffect } from "react";

/* ─── Types ────────────────────────────────────────────────────── */
export interface Appointment {
  id: string;
  tenant_id: string;
  client_id: string | null;
  lead_id: string | null;
  contract_id: string | null;
  job_id: string | null;
  title: string;
  appointment_type: string | null;
  status: string;
  start_at: string;
  end_at: string | null;
  location: string | null;
  notes: string | null;
  show_in_portal: boolean;
  created_at: string;
  updated_at: string;
}

export interface ClientOption { id: string; full_name: string; email: string; }
export interface LeadOption   { id: string; name: string; }
export interface ContractOption { id: string; title: string; contract_number: string; }
export interface JobOption    { id: string; title: string; }

interface Props {
  appointment: Appointment | null;
  isNew: boolean;
  clients: ClientOption[];
  leads: LeadOption[];
  contracts: ContractOption[];
  jobs: JobOption[];
  prefillStartAt?: string;   // ← NEW: "YYYY-MM-DDThh:mm" or full ISO
  onClose: () => void;
  onSave: (data: Appointment) => void;
}

const APPOINTMENT_TYPES = [
  "Consultation",
  "Shoot Planning",
  "Proof Review",
  "Contract Signing",
  "Follow-up",
  "Album Review",
  "Print Pickup",
  "Other",
];

const STATUSES = [
  { value: "scheduled",  label: "Scheduled" },
  { value: "confirmed",  label: "Confirmed" },
  { value: "completed",  label: "Completed" },
  { value: "cancelled",  label: "Cancelled" },
  { value: "no_show",    label: "No Show" },
];

const toLocalDatetime = (iso: string | null) => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const emptyForm = (): Omit<Appointment, "id" | "tenant_id" | "created_at" | "updated_at"> => ({
  client_id: null,
  lead_id: null,
  contract_id: null,
  job_id: null,
  title: "",
  appointment_type: null,
  status: "scheduled",
  start_at: "",
  end_at: null,
  location: null,
  notes: null,
  show_in_portal: true,
});

export default function AppointmentModal({ appointment, isNew, clients, leads, contracts, jobs, prefillStartAt, onClose, onSave }: Props) {
  const [form, setForm] = useState(emptyForm());

  useEffect(() => {
    if (appointment && !isNew) {
      setForm({
        client_id:        appointment.client_id,
        lead_id:          appointment.lead_id,
        contract_id:      appointment.contract_id,
        job_id:           appointment.job_id,
        title:            appointment.title,
        appointment_type: appointment.appointment_type,
        status:           appointment.status,
        start_at:         appointment.start_at,
        end_at:           appointment.end_at,
        location:         appointment.location,
        notes:            appointment.notes,
        show_in_portal:   appointment.show_in_portal,
      });
    } else {
      const base = emptyForm();
      if (prefillStartAt) base.start_at = prefillStartAt;
      setForm(base);
    }
  }, [appointment, isNew, prefillStartAt]);

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim() || !form.start_at) return;
    onSave({
      id:         appointment?.id ?? "",
      tenant_id:  appointment?.tenant_id ?? "",
      created_at: appointment?.created_at ?? new Date().toISOString(),
      updated_at: new Date().toISOString(),
      ...form,
      start_at:   new Date(form.start_at).toISOString(),
      end_at:     form.end_at ? new Date(form.end_at).toISOString() : null,
      title:      form.title.trim(),
      location:   form.location?.trim() || null,
      notes:      form.notes?.trim() || null,
      appointment_type: form.appointment_type || null,
      client_id:  form.client_id || null,
      lead_id:    form.lead_id || null,
      contract_id: form.contract_id || null,
      job_id:     form.job_id || null,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm">
      <div className="bg-white rounded-2xl border border-slate-200 w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#C8A96E]/10 text-[#C8A96E]">
              <i className="ri-calendar-event-line text-base"></i>
            </div>
            <h2 className="text-[#0f172a] font-semibold text-base">
              {isNew ? "New Appointment" : "Edit Appointment"}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Body */}
        <form id="appt-form" onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

          {/* Title */}
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1.5">
              Title <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => set("title", e.target.value)}
              placeholder="e.g. Wedding Consultation — Smith Family"
              required
              className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50 transition-colors"
            />
          </div>

          {/* Type + Status */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1.5">Type</label>
              <select
                value={form.appointment_type ?? ""}
                onChange={(e) => set("appointment_type", e.target.value || null)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] outline-none focus:border-[#C8A96E]/50 transition-colors bg-white cursor-pointer"
              >
                <option value="">— Select type —</option>
                {APPOINTMENT_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1.5">Status</label>
              <select
                value={form.status}
                onChange={(e) => set("status", e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] outline-none focus:border-[#C8A96E]/50 transition-colors bg-white cursor-pointer"
              >
                {STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Start / End */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1.5">
                Start <span className="text-red-400">*</span>
              </label>
              <input
                type="datetime-local"
                value={form.start_at ? toLocalDatetime(form.start_at) : ""}
                onChange={(e) => set("start_at", e.target.value)}
                required
                className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] outline-none focus:border-[#C8A96E]/50 transition-colors"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1.5">End</label>
              <input
                type="datetime-local"
                value={form.end_at ? toLocalDatetime(form.end_at) : ""}
                onChange={(e) => set("end_at", e.target.value || null)}
                min={form.start_at ? toLocalDatetime(form.start_at) : undefined}
                className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] outline-none focus:border-[#C8A96E]/50 transition-colors"
              />
            </div>
          </div>

          {/* Location */}
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1.5">Location</label>
            <input
              type="text"
              value={form.location ?? ""}
              onChange={(e) => set("location", e.target.value || null)}
              placeholder="Studio, Google Meet, client address…"
              className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50 transition-colors"
            />
          </div>

          {/* Client + Lead */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1.5">Client (optional)</label>
              <select
                value={form.client_id ?? ""}
                onChange={(e) => set("client_id", e.target.value || null)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] outline-none focus:border-[#C8A96E]/50 transition-colors bg-white cursor-pointer"
              >
                <option value="">— None —</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>{c.full_name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1.5">Lead (optional)</label>
              <select
                value={form.lead_id ?? ""}
                onChange={(e) => set("lead_id", e.target.value || null)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] outline-none focus:border-[#C8A96E]/50 transition-colors bg-white cursor-pointer"
              >
                <option value="">— None —</option>
                {leads.map((l) => (
                  <option key={l.id} value={l.id}>{l.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Contract + Job */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1.5">Contract (optional)</label>
              <select
                value={form.contract_id ?? ""}
                onChange={(e) => set("contract_id", e.target.value || null)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] outline-none focus:border-[#C8A96E]/50 transition-colors bg-white cursor-pointer"
              >
                <option value="">— None —</option>
                {contracts.map((c) => (
                  <option key={c.id} value={c.id}>{c.title} ({c.contract_number})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1.5">Job / Shoot (optional)</label>
              <select
                value={form.job_id ?? ""}
                onChange={(e) => set("job_id", e.target.value || null)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] outline-none focus:border-[#C8A96E]/50 transition-colors bg-white cursor-pointer"
              >
                <option value="">— None —</option>
                {jobs.map((j) => (
                  <option key={j.id} value={j.id}>{j.title}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1.5">Notes</label>
            <textarea
              value={form.notes ?? ""}
              onChange={(e) => set("notes", e.target.value || null)}
              rows={3}
              maxLength={500}
              placeholder="Internal notes, agenda, reminders…"
              className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50 transition-colors resize-none"
            />
            <p className="text-right text-[10px] text-slate-300 mt-1">{(form.notes ?? "").length}/500</p>
          </div>

          {/* Show in Portal */}
          <label className="flex items-center gap-3 cursor-pointer select-none">
            <div
              onClick={() => set("show_in_portal", !form.show_in_portal)}
              className={`w-10 h-5 rounded-full transition-colors relative cursor-pointer ${form.show_in_portal ? "bg-[#C8A96E]" : "bg-slate-200"}`}
            >
              <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${form.show_in_portal ? "translate-x-5" : "translate-x-0.5"}`} />
            </div>
            <span className="text-sm text-slate-600">Show in client portal</span>
          </label>

        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-100">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="appt-form"
            className="px-5 py-2 rounded-lg text-sm font-semibold bg-[#C8A96E] hover:bg-[#b8975e] text-white transition-colors cursor-pointer whitespace-nowrap"
          >
            {isNew ? "Create Appointment" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}